import React, { createContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import reportWebVitals from './reportWebVitals'
import axios from 'axios'
import { createTheme, ThemeProvider } from '@mui/material'
import { createBrowserRouter, RouterProvider } from 'react-router'
import routes from './routes'
import { loginRequest, msalConfig } from './authConfig'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { repCodeType } from './types'

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL
dayjs.extend(utc)

const msalInstance = new PublicClientApplication(msalConfig)
const router = createBrowserRouter(routes)

export const dataContext = createContext({
  user: {
    email: null as string | null,
    displayName: null as string | null,
    role: {
      admin: false,
      operations: false,
      sales: false,
    },
  },
  repCodes: [] as repCodeType[],
})

axios.interceptors.request.use(
  async config => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        account: msalInstance.getActiveAccount() ?? undefined,
        forceRefresh: true,
      })

      config.headers.Authorization = response.accessToken
      return config
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        })

        config.headers.Authorization = response.accessToken
        return config
      } else {
        throw error
      }
    }
  },
  error => {
    return Promise.reject(error)
  },
)

const palette = createTheme({
  palette: {
    primary: {
      main: '#32578b',
      contrastText: '#FFFFFF',
    },
  },
})

const theme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily: 'Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'white',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          height: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            background: 'white',
          },
        },
      },
    },
  },
  palette: palette.palette,
})

function App() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()

  const [user, setUser] = useState<{
    email: string | null
    displayName: string | null
    role: {
      admin: boolean
      operations: boolean
      sales: boolean
    }
  }>({
    email: null,
    displayName: null,
    role: {
      admin: false,
      operations: false,
      sales: false,
    },
  })
  const [repCodes, setRepCodes] = useState<repCodeType[]>([])

  useEffect(() => {
    if (inProgress !== 'none') {
      return
    }

    if (isAuthenticated) {
      instance.setActiveAccount(instance.getAllAccounts()[0])

      if (!user.email) {
        axios
          .get(`/userRole/${instance.getActiveAccount()?.username}`)
          .then(res => {
            setUser({
              email: instance.getActiveAccount()?.username ?? null,
              displayName: instance.getActiveAccount()?.name ?? null,
              role: res.data,
            })
          })
          .catch(err => {
            console.error(err)
            setUser({
              email: instance.getActiveAccount()?.username ?? null,
              displayName: instance.getActiveAccount()?.name ?? null,
              role: {
                admin: false,
                operations: false,
                sales: false,
              },
            })
          })
      }
      if (repCodes.length === 0) {
        axios
          .get('/repCodes')
          .then(res => {
            setRepCodes(res.data)
          })
          .catch(err => {
            console.error(err)
          })
      }
    } else {
      instance.loginRedirect(loginRequest).catch(e => {
        console.log(e)
      })

      instance.handleRedirectPromise().then(() => {
        if (instance.getAllAccounts().length > 0) {
          instance.setActiveAccount(instance.getAllAccounts()[0])
          axios
            .get(`/userRole/${instance.getActiveAccount()?.username}`)
            .then(res => {
              setUser({
                email: instance.getActiveAccount()?.username ?? null,
                displayName: instance.getActiveAccount()?.name ?? null,
                role: res.data,
              })
            })
            .catch(err => {
              console.error(err)
              setUser({
                email: instance.getActiveAccount()?.username ?? null,
                displayName: instance.getActiveAccount()?.name ?? null,
                role: {
                  admin: false,
                  operations: false,
                  sales: false,
                },
              })
            })
        }
      })
    }
  }, [inProgress, isAuthenticated, instance])

  return isAuthenticated ? (
    <dataContext.Provider value={{ user, repCodes }}>
      <RouterProvider router={router} />
    </dataContext.Provider>
  ) : (
    <div>
      <p>Please sign in</p>
    </div>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </MsalProvider>
  </React.StrictMode>,
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
